import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtEUR = (n) =>
  new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(n || 0);

const fmtCount = (n) => new Intl.NumberFormat('de-DE').format(n || 0);

const KPICard = ({ label, value, sub, color = 'bg-white', icon, to, testid, badge }) => {
  const content = (
    <div className={`relative rounded-xl ${color} p-5 border border-gray-100 hover:shadow-md transition group`} data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
        {icon && (
          <span className="w-8 h-8 rounded-lg bg-primary/5 flex items-center justify-center text-primary">
            {icon}
          </span>
        )}
      </div>
      <div className="text-2xl sm:text-3xl font-bold text-primary leading-tight">{value}</div>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      {badge !== undefined && badge > 0 && (
        <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-amber-500 text-white text-xs font-bold">
          {badge}
        </span>
      )}
      {to && (
        <span className="absolute bottom-3 right-4 text-xs text-accent opacity-0 group-hover:opacity-100 transition">
          öffnen →
        </span>
      )}
    </div>
  );

  return to ? <Link to={to} className="block">{content}</Link> : content;
};

const Skeleton = () => (
  <div className="rounded-xl bg-white p-5 border border-gray-100 animate-pulse">
    <div className="h-3 bg-gray-200 rounded w-20 mb-3"></div>
    <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
    <div className="h-3 bg-gray-200 rounded w-32"></div>
  </div>
);

const AdminKPIs = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`);
      setStats(res.data);
    } catch (e) {
      console.error('Failed to load admin stats', e);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6" data-testid="admin-kpis-loading">
        {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} />)}
      </div>
    );
  }

  if (!stats) return null;

  const today = new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <section className="mb-8" data-testid="admin-kpis">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-primary">Übersicht</h2>
          <p className="text-xs text-muted-foreground capitalize">{today}</p>
        </div>
        <button onClick={load} className="text-xs text-accent hover:underline" data-testid="admin-kpis-refresh">
          Aktualisieren
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <KPICard
          label="Gesamteinlage"
          value={fmtEUR(stats.investments.total_principal_eur)}
          sub={`${stats.investments.count} Investments`}
          color="bg-gradient-to-br from-amber-50 to-amber-100/50"
          testid="kpi-total-principal"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <KPICard
          label="Kunden"
          value={fmtCount(stats.customers.active)}
          sub={stats.customers.total > stats.customers.active ? `${stats.customers.total - stats.customers.active} pending` : 'alle aktiv'}
          testid="kpi-customers"
          to="/admin"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          }
        />
        <KPICard
          label="Fällige Auszahlungen"
          value={fmtEUR(stats.payouts.due_sum_eur)}
          sub={`${stats.payouts.due_count} offen`}
          color={stats.payouts.due_count > 0 ? 'bg-gradient-to-br from-red-50 to-red-100/40' : 'bg-white'}
          testid="kpi-due-payouts"
          to="/admin/payouts"
          badge={stats.payouts.due_count}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          }
        />
        <KPICard
          label="Vollmachten"
          value={fmtCount(stats.authorizations.active)}
          sub={
            stats.authorizations.pending_admin_review > 0
              ? `${stats.authorizations.pending_admin_review} zur Prüfung`
              : 'alle aktiv'
          }
          color={stats.authorizations.pending_admin_review > 0 ? 'bg-gradient-to-br from-blue-50 to-blue-100/40' : 'bg-white'}
          testid="kpi-authorizations"
          to="/admin/vollmachten"
          badge={stats.authorizations.pending_admin_review}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          }
        />
        <KPICard
          label="Pending Registrierungen"
          value={fmtCount(stats.pending_registrations)}
          sub={stats.pending_registrations > 0 ? 'Warten auf Freigabe' : 'keine offenen'}
          color={stats.pending_registrations > 0 ? 'bg-gradient-to-br from-amber-50 to-orange-100/40' : 'bg-white'}
          testid="kpi-pending-registrations"
          to="/admin/registrierungen"
          badge={stats.pending_registrations}
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          }
        />
        <KPICard
          label="Bestätigte Auszahlungen"
          value={fmtEUR(stats.payouts.confirmed_sum_eur)}
          sub="gesamt bisher"
          testid="kpi-confirmed-payouts"
          color="bg-gradient-to-br from-green-50 to-green-100/40"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          }
        />
        <KPICard
          label="Aktive Vollmachten"
          value={fmtCount(stats.authorizations.active)}
          sub="laufend"
          testid="kpi-active-authorizations"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
        <KPICard
          label="Investments"
          value={fmtCount(stats.investments.count)}
          sub="aktive Verträge"
          testid="kpi-investments"
          icon={
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H5a2 2 0 01-2-2V5a2 2 0 012-2h14a2 2 0 012 2v14a2 2 0 01-2 2z" />
            </svg>
          }
        />
      </div>
    </section>
  );
};

export default AdminKPIs;
